import { Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

import { BodilColor, NorlysColor, NorlysFont } from '~src/branding';
import { Heading } from '~src/components/Typography';
import { UnderlineNorlys } from '~src/components/UnderlineNorlys';
import { monthNames } from '~src/constants/date';

import { Legend, LegendNorlys } from '../Legend';
import { EnergyCostGraphTooltip } from '../Tooltip/EnergyCostTooltip';
import { DEFAULT_TITLE } from './constants';
import { MobileTitleWrapper, MobileTitleWrapperNorlys, TitleNorlys } from './style';
import { EnergyCostGraphProps, MobileEnergyCostGraphProps } from './types';

const MobileEnergyCostGraphCommon = ({
  values,
  beforeColor = BodilColor.ui.action.default,
  afterColor = BodilColor.base.default,
  variant = 'default',
  tooltipProps,
}: MobileEnergyCostGraphProps) => {
  const data = values.map(([before, after], idx) => ({
    month: monthNames[idx],
    annualPriceBefore: before,
    annualPriceAfter: after,
  }));

  if (!data) {
    return <></>;
  }

  const lineProps = { strokeWidth: variant === 'wide' ? 6 : 3, dot: false, activeDot: true };

  return (
    <ResponsiveContainer width='100%' height={200}>
      <LineChart width={100} height={100} data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <Line
          {...{ type: 'monotone', dataKey: 'annualPriceAfter', name: 'Fremtidig', stroke: afterColor, ...lineProps }}
        />
        <Line
          {...{ type: 'monotone', dataKey: 'annualPriceBefore', name: 'Nuværende', stroke: beforeColor, ...lineProps }}
        />
        <RechartsTooltip
          cursor={false}
          content={<EnergyCostGraphTooltip {...{ beforeColor, afterColor, ...tooltipProps }} />}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export const MobileEnergyCostGraph = ({
  values,
  variant = 'default',
  legendEntries,
  title = DEFAULT_TITLE,
  ...rest
}: EnergyCostGraphProps) => {
  return (
    <>
      <MobileTitleWrapper>
        {typeof title === 'string' ? <Heading level={2}>{title}</Heading> : title}
      </MobileTitleWrapper>
      <Legend entries={legendEntries} direction='column' />
      <MobileEnergyCostGraphCommon {...{ values, variant, ...rest }} />
    </>
  );
};

export const MobileEnergyCostGraphNorlys = ({
  values,
  variant,
  legendEntries,
  title = DEFAULT_TITLE,
}: EnergyCostGraphProps) => {
  return (
    <>
      <MobileTitleWrapperNorlys>
        {typeof title === 'string' ? <TitleNorlys>{title}</TitleNorlys> : title}

        <UnderlineNorlys {...{ style: { margin: '16px 0' } }} />
      </MobileTitleWrapperNorlys>
      <LegendNorlys entries={legendEntries} direction='column' />
      <MobileEnergyCostGraphCommon
        {...{
          beforeColor: NorlysColor.secondary.lightPurple,
          afterColor: NorlysColor.secondary.purple,
          values,
          variant,
          tooltipProps: { beforeTextColor: 'black', fontStyle: NorlysFont.style('heading', 14) },
        }}
      />
    </>
  );
};
